/**
 * f14.2: the queue's stat tiles (design D8) -- Pending, Accepted, Rejected,
 * Superseded, and the pending variance per currency. Counts come from the
 * list answer (they honour every filter except status); the baseline comes
 * from the summary. Currencies are never added together (BR-F14.2-10).
 */

import type { ReconciliationSummary, ReconciliationVarianceList } from '@/api/client';

import { formatAmount, monthLabel, varianceClass } from './reconciliationFormat';

function Tile({ label, bar, value, foot }: { label: string; bar: number; value: React.ReactNode; foot?: React.ReactNode }) {
  return (
    <div className={`card stat-card rv-bar-${bar}`}>
      <div className="stat-top"><span className="stat-label">{label}</span></div>
      <div className="stat-value sm">{value}</div>
      {foot && <div className="stat-foot">{foot}</div>}
    </div>
  );
}

function heldMonths(months: readonly number[]): string {
  if (!months.length) return 'none';
  const first = months[0] ?? 1;
  const last = months[months.length - 1] ?? first;
  return first === last ? monthLabel(first) : `${monthLabel(first)}–${monthLabel(last)}`;
}

export function ReconciliationSummaryCard({
  list,
  summary,
}: {
  list: ReconciliationVarianceList | undefined;
  summary: ReconciliationSummary | undefined;
}) {
  const counts = list?.counts ?? summary?.counts_by_status;
  const totals = list?.pending_totals ?? [];
  const baseline = summary?.baseline ?? null;
  const bySource = summary?.counts_by_source;

  return (
    <div className="grid grid-5 mb-4 rc-summary">
      <Tile
        label="Pending"
        bar={1}
        value={counts?.PENDING ?? '—'}
        foot={bySource ? `${bySource.ACTUALS} actuals · ${bySource.FORECAST_DRIFT} drift raised` : undefined}
      />
      <Tile label="Accepted" bar={2} value={counts?.ACCEPTED ?? '—'} foot="Closed month set to the incoming figure" />
      <Tile label="Rejected" bar={3} value={counts?.REJECTED ?? '—'} foot="Baseline kept" />
      <Tile label="Superseded" bar={4} value={counts?.SUPERSEDED ?? '—'} foot="Overtaken by a later figure" />
      <Tile
        label="Pending variance"
        bar={5}
        value={
          totals.length ? (
            <span className="rc-totals">
              {totals.map((total) => (
                <span key={total.currency_code} className={varianceClass(total.variance_amount)}>
                  {formatAmount(total.variance_amount, total.currency_code, { signed: true })}
                </span>
              ))}
            </span>
          ) : (
            '—'
          )
        }
        foot={baseline ? `Held at the frozen baseline: ${heldMonths(baseline.held_months)}` : 'No frozen baseline'}
      />
    </div>
  );
}
